import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Search, ArrowRight } from "lucide-react";

// Add CSS animation for simple roll-open effect
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes rollOpen {
    0% {
      max-height: 0;
      opacity: 0;
    }
    100% {
      max-height: 85vh;
      opacity: 1;
    }
  }
`;
if (!document.head.querySelector("style[data-bottom-modal]")) {
    styleSheet.setAttribute("data-bottom-modal", "true");
    document.head.appendChild(styleSheet);
}

export interface BottomModalItem {
    id: string;
    title: string;
    description?: string;
    subtitle?: string;
    customContent?: React.ReactNode;
}

export interface BottomModalSection {
    title?: string;
    items: BottomModalItem[];
}

interface BottomModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    sections: BottomModalSection[];
    selectedItem?: BottomModalItem | null;
    onItemSelect: (item: BottomModalItem) => void;
    searchPlaceholder?: string;
    showSearch?: boolean;
    className?: string;
    customContent?: React.ReactNode;
    containerPositioned?: boolean;
    config?: {
        primaryColor: string;
    };
    footerButtons?: Array<{
        label: string;
        onClick: () => void;
        variant?: 'primary' | 'secondary' | 'danger';
        disabled?: boolean;
        style?: React.CSSProperties;
        className?: string;
    }>;
}

const BottomModal: React.FC<BottomModalProps> = ({
    isOpen,
    onClose,
    title,
    sections,
    selectedItem,
    onItemSelect,
    searchPlaceholder = "Search...",
    showSearch = true,
    customContent,
    containerPositioned = false,
    footerButtons = [],
    config,
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [visible, setVisible] = useState(false);
    const [animating, setAnimating] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setVisible(true);
            // Trigger enter animation on next frame
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setAnimating(true));
            });
        } else {
            // Trigger leave animation, then unmount
            setAnimating(false);
            const timer = setTimeout(() => setVisible(false), 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            document.body.style.overflow = "hidden";
        }
        return () => {
            document.removeEventListener("keydown", handleEscape);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, onClose]);

    // Filter sections and items based on search query
    const filteredSections = React.useMemo(() => {
        if (!searchQuery.trim()) return sections;

        return sections
            .map((section) => ({
                ...section,
                items: section.items.filter(
                    (item) =>
                        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (item.description &&
                            item.description
                                .toLowerCase()
                                .includes(searchQuery.toLowerCase())) ||
                        (item.subtitle &&
                            item.subtitle.toLowerCase().includes(searchQuery.toLowerCase())),
                ),
            }))
            .filter((section) => section.items.length > 0);
    }, [sections, searchQuery]);

    if (!visible) return null;

    const handleItemClick = (item: BottomModalItem) => {
        onItemSelect(item);
        onClose();
    };

    const modalContent = (
        <div className={`${containerPositioned ? 'absolute inset-0' : 'fixed inset-0'} z-50`}>
            {/* Backdrop */}
            <div
                className={`absolute inset-0 transition-opacity duration-300 ease-in-out bg-black/40 dark:bg-black/60 backdrop-blur-sm ${animating ? "opacity-100" : "opacity-0"}`}
                onClick={onClose}
            />

            {/* Modal Container */}
            <div
                className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-900 w-full rounded-t-3xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-[60vh] transition-all duration-250 ease-in-out flex flex-col"
                style={{
                    maxHeight: animating ? "60vh" : "0",
                    opacity: animating ? 1 : 0,
                    transition: "max-height 0.5s ease-out, opacity 0.3s ease-out",
                }}
            >
                {/* Fixed Header */}
                <div className="shrink-0">
                    <div className="flex justify-center py-2">
                        <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
                    </div>

                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {title}
                        </h2>
                        <div
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                            aria-label="Close modal"
                        >
                            <X size={20} />
                        </div>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {customContent || (
                        <>
                            {/* Search Bar */}
                            {showSearch && (
                                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                                    <div className="relative flex items-center">
                                        <Search size={16} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        <input
                                            type="text"
                                            placeholder={searchPlaceholder}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                            autoFocus
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Sections */}
                            {filteredSections.length === 0 ? (
                                <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-300">
                                    No results found
                                </div>
                            ) : (
                                filteredSections.map((section, sectionIndex) => (
                                    <div key={sectionIndex}>
                                        {/* Section Title */}
                                        {section.title && (
                                            <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-widest bg-gray-50/50 dark:bg-gray-900/50 border-y border-gray-100 dark:border-gray-700 first:border-t-0">
                                                {section.title}
                                            </div>
                                        )}

                                        {/* Section Items */}
                                        {section.items.map((item) => {
                                            const isSelected = selectedItem?.id === item.id;
                                            const isHovered = hoveredId === item.id;

                                            return (
                                                <button
                                                    key={item.id}
                                                    onMouseEnter={() => setHoveredId(item.id)}
                                                    onMouseLeave={() => setHoveredId(null)}
                                                    onClick={() => handleItemClick(item)}
                                                    className={`w-full px-4 py-3 text-left transition-all flex items-center bg-white gap-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${isSelected ? "bg-gray-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-semibold" : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white"}`}
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        <div className="truncate">{item.title}</div>
                                                        {item.subtitle && (
                                                            <div className="text-sm text-gray-500 dark:text-gray-300 truncate">
                                                                {item.subtitle}
                                                            </div>
                                                        )}
                                                        {item.description && (
                                                            <div className="text-xs text-gray-400 dark:text-gray-400 truncate">
                                                                {item.description}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Radio Button */}
                                                    <div className="relative">
                                                        <div
                                                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? "border-blue-500 bg-blue-500" : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"}`}
                                                        >
                                                            {isSelected && (
                                                                <div className="w-4 h-4 rounded-full bg-white" />
                                                            )}
                                                        </div>
                                                    </div>

                                                    {isHovered && !isSelected && (
                                                        <ArrowRight size={16} className=" text-gray-400 animate-in fade-in slide-in-from-left-2 duration-200" />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ))
                            )}
                        </>
                    )}
                </div>

                {/* Fixed Footer */}
                {footerButtons && footerButtons.length > 0 && (
                    <div className="shrink-0 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                        <div className="flex gap-2">
                            {footerButtons.map((button, index) => {
                                const baseClasses = "flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2";

                                let variantClasses = "";
                                const finalStyle = button.style || {};
                                const finalClassName = button.className
                                    ? `${baseClasses} ${button.className}`
                                    : `${baseClasses} ${variantClasses}`;

                                if (!button.className) {
                                    switch (button.variant) {
                                        case 'primary':
                                            if (config?.primaryColor) {
                                                variantClasses = "text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed";
                                                finalStyle.backgroundColor = config.primaryColor;
                                            } else {
                                                variantClasses = "bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed";
                                            }
                                            break;
                                        case 'danger':
                                            variantClasses = "bg-red-500 text-white hover:bg-red-600 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed";
                                            break;
                                        case 'secondary':
                                        default:
                                            variantClasses = "bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed";
                                            break;
                                    }
                                }

                                return (
                                    <button
                                        key={index}
                                        onClick={button.onClick}
                                        disabled={button.disabled}
                                        className={finalClassName}
                                        style={finalStyle}
                                    >
                                        {button.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const portalTarget = document.getElementById("chatbot-dedicated-container"); // Changed from chatbot-widget-container

    if (portalTarget) {
        return createPortal(modalContent, portalTarget);
    }

    if (containerPositioned) {
        return modalContent;
    }

    return createPortal(modalContent, document.body);
};

export default BottomModal;
